import React, { useState, useEffect } from "react";
import axios from "axios";
import { useShop } from "../context/ShopContext";
import { useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area,
  PieChart, Pie, Cell,
} from "recharts";

/* ─── helpers ────────────────────────────────────────────────── */
const DASHBOARD_API = "https://winenew.sipstocks.in/api/Liquor/GetDashboardSummary";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
};

const fmtINR = (v) => {
  if (v == null || isNaN(v)) return "₹0";
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
};

const fmtNum = (v) => (v == null ? "0" : Math.round(v).toLocaleString("en-IN"));

const DONUT_COLORS = ["#F59E0B", "#6366F1"];

/* ─── sub-components ─────────────────────────────────────────── */
const TopCard = ({ label, value, icon, accent }) => (
  <div style={{
    flex: "1 1 160px", background: "#fff", borderRadius: 14,
    padding: "18px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
    display: "flex", flexDirection: "column", gap: 10,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 9, background: accent + "1a",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
      }}>{icon}</div>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>
    </div>
    <div style={{ fontSize: 23, fontWeight: 800, color: "#111827", letterSpacing: -0.5 }}>{value}</div>
  </div>
);

const PerfTile = ({ label, value, icon, accent }) => (
  <div style={{
    flex: "1 1 120px", background: "#fff", borderRadius: 10,
    padding: "14px 14px", boxShadow: "0 1px 5px rgba(0,0,0,0.06)",
    borderLeft: `3px solid ${accent}`,
  }}>
    <div style={{ fontSize: 10, fontWeight: 600, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
      {icon}&nbsp;{label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{value}</div>
  </div>
);

/* ─── main component ─────────────────────────────────────────── */
export default function Home() {
  const { shopId, companyId, userName, shopName } = useShop();
  const navigate = useNavigate();

  const [todayData,      setTodayData]      = useState(null);
  const [monthlyData,    setMonthlyData]    = useState(null);
  const [allTimeTotal,   setAllTimeTotal]   = useState(0);
  const [chartRows,      setChartRows]      = useState([]);
  const [weeklyChartData,  setWeeklyChartData]  = useState([]);
  const [monthlyChartData, setMonthlyChartData] = useState([]);
  const [chartPeriod,    setChartPeriod]    = useState("Week");
  const [donutSource,    setDonutSource]    = useState(null);
  const [loading,        setLoading]        = useState(true);

  useEffect(() => {
    if (!shopId) return;
    setLoading(true);
    axios.post(
      DASHBOARD_API,
      { shopId, companyId, date: new Date().toISOString() },
      { headers: { Accept: "*/*", "Content-Type": "application/json" } }
    )
      .then((res) => {
        const d = res.data?.data;
        if (!d) return;
        setTodayData(d.today);
        setMonthlyData(d.monthly);
        setAllTimeTotal(d.allTimeTotal || 0);
        setWeeklyChartData(d.weeklyChart || []);
        setMonthlyChartData(d.monthlyChart || []);
        setChartRows(d.weeklyChart || []);
        setDonutSource(d.donutSource);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [shopId, companyId]); // eslint-disable-line

  const handlePeriod = (p) => {
    setChartPeriod(p);
    setChartRows(p === "Week" ? weeklyChartData : monthlyChartData);
  };

  const donutData = donutSource
    ? [
        { name: "Desi",    value: donutSource.desi },
        { name: "English", value: donutSource.english },
      ]
    : [];

  const nowStr = new Date().toLocaleString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });

  const T = todayData;
  const M = monthlyData;

  return (
    <div className="home-page-wrapper" style={{ background: "#F5F6FA", minHeight: "100vh", padding: "22px 24px 48px" }}>

      {/* ── Greeting header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 26, flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 26 }}>🍷</span>
            <h4 style={{ margin: 0, fontWeight: 800, fontSize: 22, color: "#111827" }}>
              {greeting()}, {userName || "Admin"}!
            </h4>
          </div>
          <div style={{ color: "#6B7280", fontSize: 13, marginTop: 4, marginLeft: 36 }}>{nowStr}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{
            background: "#D1FAE5", color: "#065F46", fontSize: 12, fontWeight: 700,
            padding: "5px 12px", borderRadius: 20, display: "flex", alignItems: "center", gap: 5,
          }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#10B981", display: "inline-block" }} />
            Live
          </span>
          <span style={{ background: "#EEF2FF", color: "#4F46E5", fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 20 }}>
            🏪 {shopName}
          </span>
        </div>
      </div>

      {/* ── Section heading + action buttons ── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 19, color: "#111827" }}>Dashboard Overview</div>
          <div style={{ color: "#9CA3AF", fontSize: 13, marginTop: 2 }}>Comprehensive wine shop management</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => navigate("/pos")}
            style={{ background: "#4F46E5", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            📋 Add Sale
          </button>
          <button onClick={() => navigate("/boxenglish")}
            style={{ background: "#fff", color: "#374151", border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            📦 Add Stock
          </button>
          <button onClick={() => navigate("/expense")}
            style={{ background: "#fff", color: "#374151", border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            💸 Add Expense
          </button>
        </div>
      </div>

      {/* ── Top 5 stat cards ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#9CA3AF" }}>
          <FaSpinner style={{ animation: "spin 1s linear infinite", fontSize: 22 }} />&nbsp; Loading…
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
            <TopCard label="Net Cash"     value={fmtINR(T?.amount)}   icon="💵" accent="#10B981" />
            <TopCard label="Today Sale"   value={fmtINR(T?.sales)}    icon="📈" accent="#6366F1" />
            <TopCard label="Monthly Sale" value={fmtINR(M?.sales)}    icon="📅" accent="#8B5CF6" />
            <TopCard label="Total Sale"   value={fmtINR(allTimeTotal)} icon="📊" accent="#3B82F6" />
            <TopCard label="Add-on Rev"   value={fmtINR(T?.phonePay)} icon="📱" accent="#F59E0B" />
          </div>

          {/* ── Today's Performance ── */}
          <div style={{ fontWeight: 700, fontSize: 12, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            Today's Performance
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <PerfTile label="देशी Sales"    value={fmtINR(T?.desi)}       icon="🍶" accent="#F59E0B" />
            <PerfTile label="English Sales" value={fmtINR(T?.english)}    icon="🍾" accent="#6366F1" />
            <PerfTile label="Expenses"      value={fmtINR(T?.exp)}        icon="💸" accent="#EF4444" />
            <PerfTile label="Commission"    value={fmtINR(T?.commission)} icon="💼" accent="#8B5CF6" />
            <PerfTile label="Cash Flow"     value={fmtINR((T?.credit || 0) - (T?.debit || 0))} icon="💰" accent="#10B981" />
            <PerfTile label="Credit"        value={fmtINR(T?.credit)}     icon="⬆️" accent="#3B82F6" />
            <PerfTile label="Debit"         value={fmtINR(T?.debit)}      icon="⬇️" accent="#EC4899" />
            <PerfTile label="Salary"        value={fmtINR(T?.salary)}     icon="👤" accent="#06B6D4" />
          </div>
        </>
      )}

      {/* ── Charts row ── */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>

        {/* Bar chart: Desi vs English */}
        <div style={{ flex: "2 1 300px", background: "#fff", borderRadius: 14, padding: "22px 18px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>आमद vs भेजान</div>
          <div style={{ color: "#9CA3AF", fontSize: 12, marginBottom: 18 }}>Weekly stock movement</div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}><FaSpinner /></div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={chartRows} barSize={18} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}
                  formatter={(v, n) => [fmtINR(v), n === "desi" ? "देशी" : "English"]} />
                <Legend formatter={(v) => v === "desi" ? "देशी" : "English"} />
                <Bar dataKey="desi"    fill="#F59E0B" radius={[5, 5, 0, 0]} />
                <Bar dataKey="english" fill="#6366F1" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Area chart: Sales trend */}
        <div style={{ flex: "2 1 300px", background: "#fff", borderRadius: 14, padding: "22px 18px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 4 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Weekly Sales</div>
              <div style={{ color: "#9CA3AF", fontSize: 12, marginBottom: 18 }}>Revenue performance</div>
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
              {["Week", "Month"].map((p) => (
                <button key={p} onClick={() => handlePeriod(p)}
                  style={{
                    background: chartPeriod === p ? "#6366F1" : "#F3F4F6",
                    color: chartPeriod === p ? "#fff" : "#6B7280",
                    border: "none", borderRadius: 6, padding: "4px 11px",
                    fontSize: 12, fontWeight: 700, cursor: "pointer",
                  }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}><FaSpinner /></div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={chartRows}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#6366F1" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}
                  formatter={(v) => [fmtINR(v), "Sales"]} />
                <Area type="monotone" dataKey="sales" stroke="#6366F1" strokeWidth={2.5}
                  fill="url(#salesGrad)" dot={{ r: 4, fill: "#6366F1", strokeWidth: 0 }}
                  activeDot={{ r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Donut chart: Revenue breakdown */}
        <div style={{ flex: "1 1 200px", background: "#fff", borderRadius: 14, padding: "22px 18px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)", minWidth: 200 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Revenue Breakdown</div>
          <div style={{ color: "#9CA3AF", fontSize: 12, marginBottom: 10 }}>Distribution by category</div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}><FaSpinner /></div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%"
                    innerRadius={50} outerRadius={72}
                    paddingAngle={5} dataKey="value" startAngle={90} endAngle={-270}>
                    {donutData.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}
                    formatter={(v, n) => [fmtINR(v), n]} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                {donutData.map((d, i) => {
                  const total = donutSource?.sales || 0;
                  const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
                  return (
                    <div key={d.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: DONUT_COLORS[i], display: "inline-block" }} />
                        <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{d.name}</span>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{fmtINR(d.value || 0)}</span>
                        <span style={{ fontSize: 11, color: "#9CA3AF", marginLeft: 4 }}>({pct}%)</span>
                      </div>
                    </div>
                  );
                })}
                <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 8, marginTop: 2, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>Total</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>{fmtINR(donutSource?.sales || 0)}</span>
                </div>
              </div>
            </>
          )}
        </div>

      </div>

      {/* ── Quick Navigation ── */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 14 }}>
          Quick Navigation
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>

          {/* LEFT PANEL: Sales + Stock */}
          <div style={{ flex: "2 1 400px", background: "#fff", borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>

            {/* पर्चा SALES */}
            <NavSection title="पर्चा" subtitle="SALES" icon="📄" iconBg="#EEF2FF" iconColor="#4F46E5">
              <NavItem label="देशी पर्चा"           sub="Desi Invoice"     icon="📋" bg="#3B82F6" path="/desistock"       navigate={navigate} />
              <NavItem label="अंग्रेजी पर्चा"        sub="English Invoice"  icon="📋" bg="#3B82F6" path="/stockmanagement" navigate={navigate} />
              <NavItem label="पिछला देशी पर्चा"      sub="Previous Desi"    icon="📋" bg="#3B82F6" path="/desistockentry"  navigate={navigate} />
              <NavItem label="पिछला अंग्रेजी पर्चा"  sub="Previous English" icon="📋" bg="#3B82F6" path="/stock"           navigate={navigate} />
            </NavSection>

            <div style={{ borderTop: "1px solid #F3F4F6", margin: "16px 0" }} />

            {/* आमद STOCK INWARD */}
            <NavSection title="आमद" subtitle="STOCK INWARD" icon="⬇️" iconBg="#ECFDF5" iconColor="#059669">
              <NavItem label="देशी आमद"            sub="Desi Inward"    icon="⬇️" bg="#10B981" path="/editdesistockwithdate" navigate={navigate} />
              <NavItem label="अंग्रेजी आमद"         sub="English Inward" icon="⬇️" bg="#10B981" path="/editstockwithdate"    navigate={navigate} />
              <NavItem label="बॉक्स देशी आमद"      sub="Box Desi In"    icon="📦" bg="#10B981" path="/boxdesi"               navigate={navigate} />
              <NavItem label="बॉक्स अंग्रेजी आमद"  sub="Box English In" icon="📦" bg="#10B981" path="/boxenglish"            navigate={navigate} />
            </NavSection>

            <div style={{ borderTop: "1px solid #F3F4F6", margin: "16px 0" }} />

            {/* भेजान STOCK OUTWARD */}
            <NavSection title="भेजान" subtitle="STOCK OUTWARD" icon="⬆️" iconBg="#FFF7ED" iconColor="#EA580C">
              <NavItem label="देशी भेजान"          sub="Desi Outward"      icon="⬆️" bg="#F97316" path="/editdesitransferstock" navigate={navigate} />
              <NavItem label="अंग्रेजी भेजान"       sub="English Outward"   icon="⬆️" bg="#F97316" path="/edittransferstock"    navigate={navigate} />
              <NavItem label="बॉक्स देशी भेजान"    sub="Box Desi Out"      icon="📦" bg="#F97316" path="/boxdesitransfer"       navigate={navigate} />
              <NavItem label="बॉक्स अंग्रेजी भेजान" sub="Box English Out"  icon="📦" bg="#F97316" path="/boxenglishtransfer"    navigate={navigate} />
            </NavSection>
          </div>

          {/* RIGHT PANEL: Expenses + Reports */}
          <div style={{ flex: "1 1 280px", background: "#fff", borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>

            {/* खर्च EXPENSES */}
            <NavSection title="खर्च" subtitle="EXPENSES & FINANCE" icon="💸" iconBg="#FFF7ED" iconColor="#EA580C">
              <NavItem label="खर्चा एंट्री"    sub="Expense Entry"      icon="🧾" bg="#F97316" path="/expense"         navigate={navigate} />
              <NavItem label="पिछला खर्चा"    sub="Previous Expense"   icon="🧾" bg="#F97316" path="/expensedate"     navigate={navigate} />
              <NavItem label="देसी कमीशन"     sub="Desi Commission"    icon="₹" bg="#F97316" path="/commissiondesi"  navigate={navigate} />
              <NavItem label="अंग्रेजी कमीशन" sub="English Commission" icon="₹" bg="#F97316" path="/commission"      navigate={navigate} />
              <NavItem label="स्टाफ वेतन"     sub="Staff Salary"       icon="👤" bg="#F97316" path="/employee"        navigate={navigate} />
              <NavItem label="नामे / जमा"     sub="Debit / Credit"     icon="💳" bg="#F97316" path="/creditdebit"     navigate={navigate} />
              <NavItem label="दर अंतर"           sub="Rate Difference"    icon="📉" bg="#F97316" path="/ratedifference"        navigate={navigate} />
              <NavItem label="प्रोडक्ट दर अंतर" sub="Product Rate Diff"  icon="📉" bg="#F97316" path="/productratedifference" navigate={navigate} />
              <NavItem label="फोनपे जमा"       sub="PhonePe Deposit"    icon="📱" bg="#F97316" path="/phonepaycredit"         navigate={navigate} />
            </NavSection>

            <div style={{ borderTop: "1px solid #F3F4F6", margin: "16px 0" }} />

            {/* रिपोर्ट REPORTS */}
            <NavSection title="रिपोर्ट" subtitle="REPORTS" icon="📊" iconBg="#F5F3FF" iconColor="#7C3AED">
              <NavItem label="बिकी रिपोर्ट"  sub="Sales Report"    icon="📊" bg="#8B5CF6" path="/salesreport"   navigate={navigate} />
              <NavItem label="पर्चा रिपोर्ट" sub="Invoice Report"  icon="📑" bg="#8B5CF6" path="/report"        navigate={navigate} />
              <NavItem label="खर्च रिपोर्ट"  sub="Expense Report"  icon="📑" bg="#8B5CF6" path="/expensereport" navigate={navigate} />
              <NavItem label="प्रिंट पर्चा"  sub="Print Invoice"   icon="🖨️" bg="#8B5CF6" path="/printslip"     navigate={navigate} />
            </NavSection>
          </div>

        </div>
      </div>

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </div>
  );
}

/* ─── quick-nav sub-components ─────────────────────────────────── */
function NavSection({ title, subtitle, icon, iconBg, iconColor, children }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: iconColor }}>
          {icon}
        </div>
        <div>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{title} </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF" }}>({subtitle})</span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

function NavItem({ label, sub, icon, bg, path, navigate }) {
  return (
    <div
      onClick={() => navigate(path)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", borderRadius: 10, cursor: "pointer",
        border: "1px solid #F3F4F6", transition: "background 0.15s",
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 9, flexShrink: 0,
        background: `linear-gradient(135deg, ${bg}dd, ${bg})`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
        boxShadow: `0 2px 6px ${bg}55`, color: "#fff", fontWeight: 700,
      }}>
        {icon}
      </div>
      <div style={{ overflow: "hidden" }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        <div style={{ fontSize: 11, color: "#9CA3AF", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
      </div>
    </div>
  );
}
